import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, X, ChevronDown, ArrowRight, GraduationCap, Briefcase, Trophy, Calendar, CreditCard } from "lucide-react";

const LOGO_URL = "/assets/logo-abidjan.png";

const FORMATIONS_MEGA = [
  {
    group: "BBA — Bachelor",
    to: "/formations/bba",
    color: "#002362",
    icon: GraduationCap,
    items: [
      { label: "1ère année", sub: "Bac+1 — Post-bac", to: "/formations/bba/1" },
      { label: "2ème année", sub: "Bac+2", to: "/formations/bba/2" },
      { label: "3ème année", sub: "Bac+3 — Spécialisation", to: "/formations/bba/3" },
    ],
  },
  {
    group: "MBA",
    to: "/formations/mba",
    color: "#ED2939",
    icon: Briefcase,
    items: [
      { label: "1ère année", sub: "Bac+4", to: "/formations/mba/1" },
      { label: "2ème année", sub: "Bac+5 — Spécialisation", to: "/formations/mba/2" },
    ],
  },
  {
    group: "MBA Executive",
    to: "/formations/mba-executive",
    color: "#C0392B",
    icon: Trophy,
    items: [],
  },
];

const FORMATIONS_LINKS = [
  { label: "Rythmes de formation", sub: "Initial · Alternance · E-learning", to: "/formations/rythmes", icon: Calendar },
  { label: "Tarifs", sub: "Frais de scolarité", to: "/formations/tarifs", icon: CreditCard },
];

const ECOLE_MENU = [
  { label: "Mot du Président", to: "/ecole/mot-du-president" },
  { label: "Nos valeurs", to: "/ecole/nos-valeurs" },
  { label: "Pourquoi nous", to: "/pourquoi-nous" },
];

const NAV_LINKS = [
  { to: "/ecole", label: "L'École", hasEcoleMenu: true },
  { to: "/formations", label: "Formations", hasMega: true },
  { to: "/admissions", label: "Admission" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [ecoleOpen, setEcoleOpen] = useState(false);
  const [mobileFormationsOpen, setMobileFormationsOpen] = useState(false);
  const [mobileEcoleOpen, setMobileEcoleOpen] = useState(false);
  const [mobileBbaOpen, setMobileBbaOpen] = useState(false);
  const [mobileMbaOpen, setMobileMbaOpen] = useState(false);
  const megaRef = useRef(null);
  const ecoleRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isHome = pathname === "/";
  const isProgramDetail = pathname.match(/\/formations\/(bba|mba)(\/\d)?$/) || pathname.includes("/formations/mba-executive") || pathname.includes("/formations/rythmes") || pathname.includes("/formations/tarifs");
  const transparent = isHome && !scrolled && !isProgramDetail;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
    setEcoleOpen(false);
  }, [pathname]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setMegaOpen(false);
      if (ecoleRef.current && !ecoleRef.current.contains(e.target)) setEcoleOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const linkClass = (isActive) =>
    `text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors relative pb-0.5 ${
      transparent ? "text-white/90" : "text-navy-700"
    } ${isActive ? "!text-red-500" : "hover:text-red-500"}`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isProgramDetail
          ? "bg-white/95 backdrop-blur-xl border-b border-navy-100 shadow-[0_4px_30px_rgba(0,35,98,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-edge flex items-center justify-between h-20">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo-link">
          <img
            src={LOGO_URL}
            alt="ECL Campus Abidjan"
            className={`h-11 w-11 object-contain transition-all duration-500 ${transparent ? "brightness-0 invert" : ""}`}
          />
          <div className={`leading-tight hidden sm:block transition-colors duration-500 ${transparent ? "text-white" : "text-navy-700"}`}>
            <div className="font-display text-[15px] font-semibold tracking-tight">École de Commerce de Lyon</div>
            <div className="text-[9px] uppercase tracking-[0.3em] opacity-70">Campus Abidjan</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">

          {/* L'École */}
          <div ref={ecoleRef} className="relative">
            <button
              onClick={() => { setEcoleOpen((v) => !v); setMegaOpen(false); }}
              className={`flex items-center gap-1 ${linkClass(pathname === "/ecole" || pathname.startsWith("/ecole/") || pathname === "/pourquoi-nous")}`}
              data-testid="nav-link-ecole"
            >
              L'École
              <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${ecoleOpen ? "rotate-180" : ""}`} />
            </button>

            {ecoleOpen && (
              <div className="absolute top-full left-0 mt-4 w-56 bg-white border border-navy-100 shadow-xl shadow-navy-900/10 overflow-hidden">
                <div className="h-[2px] bg-gradient-to-r from-navy-500 via-red-500 to-red-500" />
                <div className="p-2">
                  <Link
                    to="/ecole"
                    onClick={() => setEcoleOpen(false)}
                    className="block px-3 py-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-navy-500 hover:text-navy-700 hover:bg-navy-50/50 transition-colors"
                  >
                    Vue d'ensemble
                  </Link>
                  <div className="h-px bg-navy-100 my-1" />
                  {ECOLE_MENU.map((it) => (
                    <Link
                      key={it.to}
                      to={it.to}
                      onClick={() => setEcoleOpen(false)}
                      className="flex items-center justify-between px-3 py-2.5 text-[13px] text-navy-700 hover:text-red-500 hover:bg-navy-50/50 transition-colors group"
                    >
                      {it.label}
                      <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Formations mega menu */}
          <div ref={megaRef} className="relative">
            <button
              onClick={() => { setMegaOpen((v) => !v); setEcoleOpen(false); }}
              className={`flex items-center gap-1 ${linkClass(pathname.startsWith("/formations"))}`}
              data-testid="nav-link-formations"
            >
              Formations
              <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
            </button>

            {megaOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[640px] bg-white border border-navy-100 shadow-2xl shadow-navy-900/15 overflow-hidden">
                <div className="h-[3px] flex">
                  <div className="flex-1 bg-navy-500" />
                  <div className="flex-1 bg-white border-t border-navy-100" />
                  <div className="flex-1 bg-red-500" />
                </div>

                <div className="col-span-3 border-b border-navy-100 px-5 py-3 bg-[#FAF8F3]">
                  <Link
                    to="/formations"
                    onClick={() => setMegaOpen(false)}
                    className="flex items-center justify-between group"
                  >
                    <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-navy-500 group-hover:text-red-500 transition-colors">Vue d'ensemble des formations</span>
                    <ArrowRight className="h-3.5 w-3.5 text-navy-400 group-hover:text-red-500 group-hover:translate-x-1 transition-all" />
                  </Link>
                </div>

                <div className="col-span-3 grid grid-cols-3 divide-x divide-navy-100">
                  {FORMATIONS_MEGA.map((col) => {
                    const Icon = col.icon;
                    return (
                      <div key={col.group} className="p-5">
                        <Link
                          to={col.to}
                          onClick={() => setMegaOpen(false)}
                          className="flex items-center gap-2 mb-4 group"
                        >
                          <div className="w-7 h-7 flex items-center justify-center shrink-0" style={{ background: col.color }}>
                            <Icon className="h-3.5 w-3.5 text-white" strokeWidth={1.8} />
                          </div>
                          <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-navy-700 group-hover:text-red-500 transition-colors">{col.group}</span>
                        </Link>

                        {col.items.length > 0 ? (
                          <ul className="space-y-1">
                            {col.items.map((item) => (
                              <li key={item.to}>
                                <Link
                                  to={item.to}
                                  onClick={() => setMegaOpen(false)}
                                  className="group flex flex-col px-2 py-2 hover:bg-navy-50/60 transition-colors"
                                >
                                  <span className="text-[13px] text-navy-700 group-hover:text-red-500 transition-colors font-medium">{item.label}</span>
                                  <span className="text-[11px] text-navy-400">{item.sub}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Link
                            to={col.to}
                            onClick={() => setMegaOpen(false)}
                            className="flex items-center gap-2 px-2 py-2 text-[13px] text-navy-700 hover:text-red-500 transition-colors group"
                          >
                            Découvrir
                            <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-navy-100 grid grid-cols-2 divide-x divide-navy-100 bg-[#FAF8F3]">
                  {FORMATIONS_LINKS.map((fl) => {
                    const Icon = fl.icon;
                    return (
                      <Link
                        key={fl.to}
                        to={fl.to}
                        onClick={() => setMegaOpen(false)}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-navy-50 transition-colors group"
                      >
                        <Icon className="h-4 w-4 text-navy-400 group-hover:text-red-500 transition-colors shrink-0" strokeWidth={1.5} />
                        <div>
                          <div className="text-[12px] font-semibold text-navy-700 group-hover:text-red-500 transition-colors">{fl.label}</div>
                          <div className="text-[10px] text-navy-400">{fl.sub}</div>
                        </div>
                        <ArrowRight className="h-3 w-3 text-navy-300 group-hover:text-red-500 ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Simple links */}
          {NAV_LINKS.filter((l) => !l.hasMega && !l.hasEcoleMenu).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "")}`}
              className={({ isActive }) => linkClass(isActive)}
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-red-500" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* CTA desktop */}
        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={() => navigate("/contact#candidater")}
            className="btn-red text-[11px] py-2.5 px-5"
            data-testid="header-cta-candidater"
          >
            Candidature
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Mobile burger */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className={`p-2 ${transparent ? "text-white" : "text-navy-700"}`}
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white border-l border-navy-100 w-[320px] overflow-y-auto p-0">
              {/* Mobile header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-navy-100">
                <div className="flex items-center gap-2.5">
                  <img src={LOGO_URL} alt="ECL" className="h-9 w-9 object-contain" />
                  <div className="font-display text-sm text-navy-700 leading-tight">
                    École de Commerce de Lyon<br />
                    <span className="text-[9px] uppercase tracking-[0.25em] text-navy-400">Campus Abidjan</span>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} aria-label="Fermer" data-testid="mobile-menu-close">
                  <X className="h-5 w-5 text-navy-700" />
                </button>
              </div>

              <nav className="flex flex-col px-4 py-4">
                {/* L'École mobile */}
                <div className="border-b border-navy-100 pb-2 mb-1">
                  <button
                    onClick={() => setMobileEcoleOpen((v) => !v)}
                    className="w-full flex items-center justify-between py-3 font-display text-xl text-navy-700"
                    data-testid="mobile-nav-link-ecole-toggle"
                  >
                    L'École
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileEcoleOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileEcoleOpen && (
                    <div className="pl-4 mb-2 space-y-1 border-l-2 border-red-500">
                      <Link to="/ecole" onClick={() => setOpen(false)} className="block text-[12px] text-navy-500 italic py-1.5" data-testid="mobile-dd-ecole-overview">Vue d'ensemble</Link>
                      {ECOLE_MENU.map((it) => (
                        <Link key={it.to} to={it.to} onClick={() => setOpen(false)} className="block text-[14px] text-navy-700 hover:text-red-500 py-2" data-testid={`mobile-dd-${it.to.replace(/\//g, "-")}`}>
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Formations mobile */}
                <div className="border-b border-navy-100 pb-2 mb-1">
                  <button
                    onClick={() => setMobileFormationsOpen((v) => !v)}
                    className="w-full flex items-center justify-between py-3 font-display text-xl text-navy-700"
                    data-testid="mobile-nav-link-formations-toggle"
                  >
                    Formations
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileFormationsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileFormationsOpen && (
                    <div className="pl-4 mb-2 space-y-1 border-l-2 border-red-500">
                      <Link to="/formations" onClick={() => setOpen(false)} className="block text-[12px] text-navy-500 italic py-1.5">Vue d'ensemble</Link>
                      {FORMATIONS_MEGA.map((g) =>
                        g.items.length > 0 ? (
                          <div key={g.group}>
                            <button
                              onClick={() => {
                                if (g.to.includes("bba")) setMobileBbaOpen((v) => !v);
                                else setMobileMbaOpen((v) => !v);
                              }}
                              className="w-full flex items-center justify-between text-[14px] text-navy-700 font-semibold py-2"
                              data-testid={`mobile-dd-${g.to.split("/").pop()}`}
                            >
                              {g.group}
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${((g.to.includes("bba") && mobileBbaOpen) || (!g.to.includes("bba") && mobileMbaOpen)) ? "rotate-180" : ""}`} />
                            </button>
                            {((g.to.includes("bba") && mobileBbaOpen) || (!g.to.includes("bba") && mobileMbaOpen)) && (
                              <div className="pl-3 ml-2 border-l border-navy-100 space-y-1">
                                <Link to={g.to} onClick={() => setOpen(false)} className="block text-[11px] text-navy-500 italic py-1">Vue d'ensemble</Link>
                                {g.items.map((c) => (
                                  <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block text-[13px] text-navy-700 hover:text-red-500 py-1.5" data-testid={`mobile-dd-${c.to.replace(/\//g, "-")}`}>
                                    {c.label} <span className="text-navy-400 text-[11px]">{c.sub}</span>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link key={g.to} to={g.to} onClick={() => setOpen(false)} className="block text-[14px] text-navy-700 hover:text-red-500 py-2" data-testid={`mobile-dd-${g.to.split("/").pop()}`}>
                            {g.group}
                          </Link>
                        )
                      )}
                      <div className="h-px bg-navy-100 my-1" />
                      {FORMATIONS_LINKS.map((fl) => (
                        <Link key={fl.to} to={fl.to} onClick={() => setOpen(false)} className="block text-[13px] text-navy-700 hover:text-red-500 py-2">
                          {fl.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Other links */}
                {NAV_LINKS.filter((l) => !l.hasMega && !l.hasEcoleMenu).map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.to.replace("/", "")}`}
                    className={({ isActive }) =>
                      `font-display text-xl py-3 border-b border-navy-100 transition-colors ${isActive ? "text-red-500" : "text-navy-700 hover:text-red-500"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}

                <button
                  onClick={() => { setOpen(false); navigate("/contact#candidater"); }}
                  className="btn-red mt-6 justify-center"
                  data-testid="mobile-cta-candidater"
                >
                  Candidature
                  <ArrowRight className="h-4 w-4" />
                </button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
